import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { showToast } from '../../utils/toast';
import { getTenantPortalLoginUrl, getTenantPortalLabel } from '../../utils/tenantPortalUrl';
import NotificationBell from '../../components/NotificationBell';

/* ─── Types ───────────────────────────────────────────────────── */
interface Property {
  id: number;
  address: string;
  postcode: string;
  property_type: string;
  monthly_rent: number;
  tenant_name: string | null;
  tenant_email: string | null;
  tenant_phone: string | null;
  rent_paid: boolean | null;
  rent_due: number | null;
  rent_record_id: number | null;
  tenancy_id: number | null;
  tenancy_start: string | null;
  tenancy_end: string | null;
  tenancy_ended_at?: string | null;
  tenancy_status?: string | null;
  days_to_expiry: number | null;
  room_number: string | null;
  amount_paid: number | null;
  amount_due: number | null;
  open_maintenance: number;
  expiring_certs: number;
  upcoming_viewings: number;
  bedrooms?: number;
  occupancy_active?: boolean;
}

/* ─── CSS ─────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');

.ld-wrap * { box-sizing: border-box; }
.ld-wrap {
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  background: #f6f7f9;
  min-height: 100vh;
}

/* ── Banner ── */
.ld-banner {
  background:
    radial-gradient(ellipse at 12% 60%, rgba(45,184,141,0.55) 0%, transparent 52%),
    radial-gradient(ellipse at 82% 18%, rgba(77,184,216,0.42) 0%, transparent 48%),
    radial-gradient(ellipse at 55% 110%, rgba(15,158,143,0.30) 0%, transparent 58%),
    linear-gradient(108deg, #0D6E4F 0%, #1D9E75 26%, #2DB88D 50%, #0F9E8F 72%, #4DB8D8 100%);
  min-height: 100px;
  padding: 0 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(13,110,86,0.15);
  position: relative;
  overflow: hidden;
}
.ld-banner::before {
  content: '';
  position: absolute; inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.88' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.045'/%3E%3C/svg%3E");
  background-size: 256px 256px;
  pointer-events: none;
}
.ld-banner-left { display: flex; align-items: center; gap: 18px; position: relative; z-index: 1; }
.ld-banner-logo {
  width: 68px; height: 68px;
  background: rgba(255,255,255,0.12); backdrop-filter: blur(20px);
  border-radius: 18px; display: flex; align-items: center; justify-content: center;
  font-size: 28px; border: 1.5px solid rgba(255,255,255,0.32);
  font-weight: 800; color: #fff; overflow: hidden; flex-shrink: 0;
  box-shadow: 0 4px 20px rgba(0,0,0,0.14), inset 0 1.5px 0 rgba(255,255,255,0.42);
  transform: translateZ(0);
  transition: transform cubic-bezier(0.34,1.56,0.64,1) 0.32s, box-shadow 0.32s;
}
.ld-banner-logo:hover {
  transform: translateZ(0) scale(1.07) translateY(-3px);
  box-shadow: 0 12px 32px rgba(0,0,0,0.22), inset 0 1.5px 0 rgba(255,255,255,0.42);
}
.ld-banner-greeting {
  color: rgba(255,255,255,0.76); font-size: 10px; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase; margin: 0 0 4px;
}
.ld-banner-title {
  color: #fff; font-size: 20px; font-weight: 800;
  letter-spacing: -0.02em; margin: 0; text-shadow: 0 1px 8px rgba(0,0,0,0.18);
}
.ld-banner-address {
  color: rgba(255,255,255,0.58); font-size: 11px; font-weight: 600;
  margin: 5px 0 0; display: flex; align-items: center; gap: 4px;
}
.ld-banner-center { position: relative; z-index: 1; text-align: center; }
.ld-banner-date { color: rgba(255,255,255,0.9); font-size: 13px; font-weight: 600; margin: 0; }
.ld-banner-right { display: flex; align-items: center; gap: 18px; position: relative; z-index: 1; }
.ld-banner-user { text-align: right; }
.ld-banner-name { color: #fff; font-size: 13px; font-weight: 700; margin: 0 0 5px; }
.ld-banner-role {
  display: inline-block;
  background: rgba(255,255,255,0.14); color: rgba(255,255,255,0.88);
  font-size: 9px; font-weight: 800; padding: 3px 10px; border-radius: 20px;
  text-transform: uppercase; letter-spacing: 0.06em;
  border: 1px solid rgba(255,255,255,0.24); backdrop-filter: blur(8px);
}
.ld-banner-notif {
  width: 40px; height: 40px; background: rgba(255,255,255,0.12);
  backdrop-filter: blur(12px); border: 1.5px solid rgba(255,255,255,0.24);
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 18px; cursor: pointer;
  transition: background cubic-bezier(0.34,1.56,0.64,1) 0.3s,
              transform cubic-bezier(0.34,1.56,0.64,1) 0.3s;
  position: relative;
}
.ld-banner-notif:hover { background: rgba(255,255,255,0.22); transform: scale(1.1) translateY(-2px); }
.ld-banner-badge {
  position: absolute; top: -3px; right: -3px; width: 18px; height: 18px;
  background: #ef4444; border: 2px solid #1D9E75; border-radius: 50%;
  font-size: 9px; font-weight: 800; color: #fff;
  display: flex; align-items: center; justify-content: center;
  animation: badgePulse 2s ease-in-out infinite;
}
@keyframes badgePulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.45); }
  50%      { box-shadow: 0 0 0 5px rgba(239,68,68,0); }
}

/* ── Content ── */
.ld-content { padding: 28px 28px 80px; }

/* ── KPI Row ── */
.ld-kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(175px, 1fr));
  gap: 14px;
  margin-bottom: 28px;
}
.ld-kpi {
  border-radius: 20px; padding: 22px 22px 20px; border: none; cursor: pointer;
  transition: transform .13s cubic-bezier(0.4,0,0.2,1), box-shadow .13s;
  position: relative; overflow: hidden;
}
.ld-kpi:hover { transform: translateY(-5px); }
.ld-kpi:active { transform: translateY(1px); }
.ld-kpi-icon { font-size: 22px; margin-bottom: 10px; display: block; }
.ld-kpi-label {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .07em; color: rgba(255,255,255,0.75); margin-bottom: 4px;
}
.ld-kpi-val {
  font-size: 28px; font-weight: 800; letter-spacing: -.02em; line-height: 1;
  margin-bottom: 4px; color: #fff; text-shadow: 0 1px 4px rgba(0,0,0,0.18);
}
.ld-kpi-sub { font-size: 11px; color: rgba(255,255,255,0.7); font-weight: 500; }

/* ── Section heading ── */
.ld-section-head { display: flex; align-items: center; gap: 12px; margin: 0 0 14px; }
.ld-section-label {
  font-size: 11px; font-weight: 700; letter-spacing: .10em;
  color: #6b7280; text-transform: uppercase; white-space: nowrap;
}
.ld-divider { flex: 1; height: 1px; background: #e9ecef; }
.ld-section-link {
  font-size: 12px; color: #3b82f6; background: none; border: none;
  cursor: pointer; font-weight: 700; font-family: inherit; white-space: nowrap;
}

/* ── Action cards ── */
.ld-actions { display: flex; flex-direction: column; gap: 8px; margin-bottom: 28px; }
.ld-action-card {
  display: flex; align-items: center; gap: 14px; border-radius: 12px;
  padding: 14px 18px; border: 1.5px solid; cursor: pointer;
  transition: transform .12s, box-shadow .12s;
}
.ld-action-card:hover { transform: translateX(4px); box-shadow: 0 4px 14px rgba(0,0,0,.07); }
.ld-action-icon { font-size: 20px; flex-shrink: 0; }
.ld-action-body { flex: 1; min-width: 0; }
.ld-action-title { font-size: 13px; font-weight: 700; margin-bottom: 2px; }
.ld-action-meta {
  font-size: 12px; color: #64748b; font-weight: 500;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ld-action-badge { font-size: 11px; font-weight: 800; padding: 3px 10px; border-radius: 20px; flex-shrink: 0; }
.ld-action-arrow { color: #94a3b8; font-size: 16px; flex-shrink: 0; }

/* ── All clear ── */
.ld-all-clear {
  display: flex; align-items: center; gap: 14px;
  background: #f0fdf4; border: 1.5px solid #bbf7d0;
  border-radius: 14px; padding: 20px 24px; margin-bottom: 28px;
}
.ld-all-clear-icon { font-size: 30px; }
.ld-all-clear-text { font-size: 15px; font-weight: 800; color: #166534; }
.ld-all-clear-sub { font-size: 12px; color: #16a34a; font-weight: 500; margin-top: 3px; }

/* ── Rent Progress bar ── */
.ld-rent-bar {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border-radius: 20px; padding: 26px 28px; margin-bottom: 28px;
  position: relative; overflow: hidden;
  box-shadow: 0 10px 30px rgba(15,23,42,0.2);
}
.ld-rent-bar::before {
  content: ''; position: absolute; top: -20%; right: -10%;
  width: 280px; height: 280px;
  background: radial-gradient(circle, rgba(13,148,136,0.15) 0%, transparent 70%);
  border-radius: 50%;
}
.ld-rent-bar-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 18px; position: relative; z-index: 1;
}
.ld-rent-bar-title { color: #fff; font-size: 16px; font-weight: 800; }
.ld-rent-bar-pct {
  background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.2);
  padding: 4px 14px; border-radius: 20px; font-size: 12px; font-weight: 700;
  color: rgba(255,255,255,0.9);
}
.ld-rent-bar-track {
  height: 10px; background: rgba(255,255,255,0.1); border-radius: 10px;
  overflow: hidden; margin-bottom: 20px; position: relative; z-index: 1;
}
.ld-rent-bar-fill { height: 100%; border-radius: 10px; transition: width 1s cubic-bezier(0.4,0,0.2,1); }
.ld-rent-bar-stats {
  display: grid; grid-template-columns: repeat(4,1fr); gap: 16px;
  position: relative; z-index: 1;
}
.ld-rbs-label {
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.08em; color: rgba(255,255,255,0.5); margin-bottom: 4px;
}
.ld-rbs-val { font-size: 17px; font-weight: 800; font-family: 'DM Mono', monospace; }

/* ── Compliance grid ── */
.ld-comp-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px;
  margin-bottom: 28px;
}
.ld-comp-card {
  background: #fff; border-radius: 14px; padding: 18px 20px;
  border: 1.5px solid #eaeef2; display: flex; align-items: center; gap: 14px;
  cursor: pointer; transition: transform .12s, box-shadow .12s;
}
.ld-comp-card:hover { transform: translateY(-3px); box-shadow: 0 4px 16px rgba(0,0,0,.07); }
.ld-comp-dot {
  width: 42px; height: 42px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; font-weight: 800; flex-shrink: 0;
}
.ld-comp-label {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: #94a3b8; margin-bottom: 3px;
}
.ld-comp-val { font-size: 15px; font-weight: 800; line-height: 1.2; }
.ld-comp-sub { font-size: 11px; color: #94a3b8; font-weight: 500; margin-top: 2px; }

/* ── Quick nav ── */
.ld-quick-nav {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 10px;
  margin-bottom: 32px;
}
.ld-nav-btn {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; padding: 18px 12px; border-radius: 14px; border: none; cursor: pointer;
  font-size: 13px; font-weight: 700; font-family: inherit; color: #fff;
  transition: transform .15s, box-shadow .15s; text-align: center;
}
.ld-nav-btn:hover { transform: translateY(-3px); }
.ld-nav-btn span:first-child { font-size: 24px; }

/* ── Modal ── */
.ld-modal-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(15,23,42,0.8); backdrop-filter: blur(8px);
  z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px;
}
.ld-modal {
  background: #fff; width: 100%; max-width: 500px; border-radius: 24px;
  overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  display: flex; flex-direction: column; max-height: 80vh;
}
.ld-modal-head {
  padding: 24px; border-bottom: 1px solid #f1f5f9;
  display: flex; justify-content: space-between; align-items: center;
}
.ld-modal-title { font-size: 18px; font-weight: 800; color: #0f172a; }
.ld-modal-close {
  background: #f1f5f9; border: none; width: 32px; height: 32px; border-radius: 50%;
  cursor: pointer; font-size: 18px; display: flex; align-items: center; justify-content: center;
}
.ld-modal-body { padding: 20px; overflow-y: auto; }
.ld-audit-list { display: flex; flex-direction: column; gap: 10px; }
.ld-audit-item {
  display: flex; gap: 14px; padding: 12px 14px; border-radius: 12px;
  border: 1.5px solid #f1f5f9; cursor: pointer; transition: background .15s;
}
.ld-audit-item:hover { background: #f8fafc; }
.ld-audit-icon { font-size: 18px; padding-top: 2px; }
.ld-audit-msg { font-size: 13px; font-weight: 700; color: #0f172a; margin-bottom: 2px; }
.ld-audit-time { font-size: 11px; color: #94a3b8; font-weight: 500; }

/* ── Animations ── */
@keyframes maintHue {
  0%,100% { filter: hue-rotate(0deg) saturate(1); }
  35%      { filter: hue-rotate(28deg) saturate(1.12); }
  70%      { filter: hue-rotate(-16deg) saturate(0.92); }
}
.ld-kpi-anim { animation: maintHue 3.5s ease-in-out infinite; }

@keyframes subtitlePulse {
  0%,100% { opacity: 1; }
  50%      { opacity: 0.6; }
}
.ld-live { animation: subtitlePulse 2.8s ease-in-out infinite; }

/* ── Tenant portal strip ── */
.ld-portal-strip {
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
  border: 1.5px solid #f9a8d4; border-radius: 14px; padding: 16px 20px; margin-bottom: 24px;
}
.ld-portal-label { font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: #9d174d; margin-bottom: 4px; }
.ld-portal-url { font-family: 'DM Mono', monospace; font-size: 13px; color: #012169; font-weight: 600; }
.ld-portal-sub { font-size: 12px; color: #831843; margin-top: 4px; }
.ld-portal-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.ld-portal-btn { background: linear-gradient(145deg, #db2777, #ec4899); color: #fff; border: none; border-radius: 9px; padding: 10px 16px; font-weight: 700; font-size: 12px; cursor: pointer; font-family: inherit; }
.ld-portal-btn-outline { background: #fff; color: #9d174d; border: 1px solid #f9a8d4; border-radius: 9px; padding: 10px 16px; font-weight: 700; font-size: 12px; cursor: pointer; text-decoration: none; font-family: inherit; }

/* ── Onboarding checklist ── */
.ld-onboard {
  margin-bottom: 24px; padding: 20px 24px; border-radius: 14px;
  background: linear-gradient(135deg, #f0fdf4, #eff6ff);
  border: 1.5px solid #bbf7d0;
}
.ld-onboard-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
.ld-onboard-title { margin: 0; font-size: 16px; font-weight: 800; color: #14532d; }
.ld-onboard-progress { font-size: 12px; font-weight: 700; color: #64748b; }
.ld-onboard-steps { display: flex; flex-direction: column; gap: 8px; }
.ld-onboard-step {
  display: flex; align-items: center; gap: 12px; padding: 10px 14px;
  background: #fff; border-radius: 10px; border: 1px solid #e5e7eb;
  cursor: pointer; transition: border-color 0.15s;
}
.ld-onboard-step:hover { border-color: #16a34a; }
.ld-onboard-step.done { opacity: 0.72; }
.ld-onboard-check { width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; flex-shrink: 0; }
.ld-onboard-check.pending { background: #f1f5f9; color: #94a3b8; border: 2px solid #cbd5e1; }
.ld-onboard-check.done { background: #dcfce7; color: #16a34a; border: 2px solid #86efac; }
.ld-onboard-label { font-size: 14px; font-weight: 700; color: #0f172a; flex: 1; }
.ld-onboard-hint { font-size: 11px; color: #64748b; margin-top: 2px; }
.ld-banner-notifs { display: flex; align-items: center; gap: 10px; }
.ld-banner-audit-btn {
  width: 40px; height: 40px; background: rgba(255,255,255,0.12);
  backdrop-filter: blur(12px); border: 1.5px solid rgba(255,255,255,0.24);
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 16px; cursor: pointer;
}
.ld-banner-audit-btn:hover { background: rgba(255,255,255,0.22); }

/* ── Responsive ── */
@media (max-width: 768px) {
  .ld-banner { flex-wrap: wrap; gap: 12px; padding: 20px; }
  .ld-banner-center { display: none; }
  .ld-content { padding: 20px 16px 60px; }
  .ld-kpi-row { grid-template-columns: repeat(2, 1fr); }
  .ld-rent-bar-stats { grid-template-columns: repeat(2, 1fr); }
  .ld-comp-grid { grid-template-columns: repeat(2, 1fr); }
  .ld-quick-nav { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 400px) {
  .ld-kpi-row { grid-template-columns: 1fr 1fr; }
  .ld-quick-nav { grid-template-columns: repeat(2, 1fr); }
}
`;

/* ─── Helpers ─────────────────────────────────────────────────── */
const fmt = (n: number) =>
  '\u00a3' + n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

const RAG: Record<'green' | 'amber' | 'red', { dot: string; text: string; border: string }> = {
  green: { dot: '#dcfce7', text: '#16a34a', border: '#bbf7d0' },
  amber: { dot: '#fef3c7', text: '#d97706', border: '#fde68a' },
  red:   { dot: '#fee2e2', text: '#dc2626', border: '#fecaca' },
};

/* ─── Component ───────────────────────────────────────────────── */
export default function LandlordDashboard() {
  const { token, role, userName } = useAuth();
  const navigate = useNavigate();

  const [properties, setProperties] = useState<Property[]>([]);
  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessLogo, setBusinessLogo] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAudit, setShowAudit] = useState(false);
  const [dashAlerts, setDashAlerts] = useState<any>(null);
  const [onboarding, setOnboarding] = useState<{ steps: any[]; complete: boolean; progress: number; total: number; bank_ready: boolean } | null>(null);

  const today = new Date().toISOString().slice(0, 7);
  const monthLabel = new Date(today + '-01').toLocaleDateString('en-GB', {
    month: 'long', year: 'numeric',
  });

  /* ── Data load ── */
  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [dashRes, bizRes, alertsRes, onboardRes] = await Promise.all([
        fetch(`${API_URL}/landlord/dashboard?month=${today}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/business`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => null),
        fetch(`${API_URL}/landlord/dashboard/alerts`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => null),
        fetch(`${API_URL}/landlord/onboarding`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => null),
      ]);

      const d = await dashRes.json();
      if (alertsRes?.ok) setDashAlerts(await alertsRes.json());
      if (onboardRes?.ok) setOnboarding(await onboardRes.json());

      if (bizRes?.ok) {
        const biz = await bizRes.json();
        const b = biz.settings || biz.data || biz;
        setBusinessName(b.name || b.business_name || b.company_name || b.trading_name || '');
        setBusinessAddress(b.address || b.business_address || b.registered_address || '');
        setBusinessLogo(b.logo_url || b.logo || b.logo_image || b.image_url || b.image || '');
      }

      setProperties(
        (d.properties || []).map((p: any) => ({
          ...p,
          monthly_rent:      Number(p.monthly_rent) || 0,
          rent_due:          p.rent_due   != null ? Number(p.rent_due)   : null,
          amount_due:        p.amount_due != null ? Number(p.amount_due) : (p.rent_due != null ? Number(p.rent_due) : null),
          amount_paid:       p.amount_paid != null ? Number(p.amount_paid) : null,
          open_maintenance:  Number(p.open_maintenance) || 0,
          expiring_certs:    Number(p.expiring_certs)   || 0,
          upcoming_viewings: Number(p.upcoming_viewings) || 0,
          days_to_expiry:    p.days_to_expiry != null ? Number(p.days_to_expiry) : null,
          tenancy_ended_at:  p.tenancy_ended_at ?? null,
          tenancy_status:    p.tenancy_status ?? null,
        }))
      );
    } finally {
      setLoading(false);
    }
  }, [token, today]);

  useEffect(() => { load(); }, [load]);

  /* ── Status helpers ── */
  const isOccupied = (p: Property) =>
    typeof p.occupancy_active === 'boolean'
      ? p.occupancy_active
      : (!!p.tenant_name || !!p.tenancy_id);

  const isPartial = (p: Property) =>
    !p.rent_paid && p.amount_paid != null && p.amount_paid > 0;

  const isOperationalLease = (p: Property) =>
    !p.tenancy_ended_at && String(p.tenancy_status || '').toLowerCase() !== 'notice_given';

  /* ── De-duplicate by property id ── */
  const totalProps = [...new Map(properties.map(p => [p.id, p])).values()];

  /* ── Financial KPIs ── */
  const uniqueAll  = totalProps.length;
  const uniqueOcc  = totalProps.filter(isOccupied).length;
  const occPct     = uniqueAll > 0 ? Math.round((uniqueOcc / uniqueAll) * 100) : 0;

  const occupied       = properties.filter(isOccupied);
  const totalDue       = occupied.reduce((s, p) => s + (p.amount_due || p.monthly_rent || 0), 0);
  const totalReceived  = properties.reduce((s, p) => {
    if (p.rent_paid)  return s + (p.amount_due || p.monthly_rent || 0);
    if (isPartial(p)) return s + (p.amount_paid || 0);
    return s;
  }, 0);
  const outstanding    = Math.max(0, totalDue - totalReceived);
  const collectPct     = totalDue > 0 ? Math.min(100, Math.round((totalReceived / totalDue) * 100)) : 100;
  const totalOpenMaint = totalProps.reduce((s, p) => s + p.open_maintenance, 0);
  const totalExpiringCerts = totalProps.reduce((s, p) => s + p.expiring_certs, 0);
  const voidProps      = totalProps.filter(p => !isOccupied(p));
  const readyToRent    = voidProps.filter((p: any) => p.ready_to_rent || p.status === 'ready_to_rent');

  /* ── Action alert data ── */
  const unpaidNow   = properties.filter(p => isOccupied(p) && !p.rent_paid && !isPartial(p));
  const partialNow  = properties.filter(isPartial);
  const leasesExp60 = properties.filter(p => isOperationalLease(p) && p.days_to_expiry != null && p.days_to_expiry >= 0 && p.days_to_expiry <= 60);
  const leasesExp90 = properties.filter(p => isOperationalLease(p) && p.days_to_expiry != null && p.days_to_expiry > 60 && p.days_to_expiry <= 90);
  const voidWithDays = voidProps.map(p => {
    const end = p.tenancy_end ? new Date(p.tenancy_end) : null;
    const days = end ? Math.max(0, Math.floor((Date.now() - end.getTime()) / 86400000)) : 0;
    return { ...p, daysEmpty: days, estLost: Math.round((p.monthly_rent || 0) * days / 30) };
  }).filter(p => p.daysEmpty > 0);

  /* ── Building-level grouping for hero text ── */
  const propGroups = properties.reduce((acc, p) => {
    if (!acc[p.address]) acc[p.address] = [];
    acc[p.address].push(p);
    return acc;
  }, {} as Record<string, Property[]>);
  const buildingCount   = Object.keys(propGroups).length;
  const unpaidBuildings = Object.values(propGroups)
    .filter(g => g.some(u => isOccupied(u) && !u.rent_paid && !isPartial(u))).length;

  const heroStatus =
    outstanding > 0
      ? { text: `${unpaidBuildings} ${unpaidBuildings === 1 ? 'property' : 'properties'} \u2014 rent outstanding this month`, color: '#dc2626', live: true }
      : voidWithDays.length > 0
        ? { text: `${buildingCount} ${buildingCount === 1 ? 'property' : 'properties'} \u2014 ${voidWithDays.length} vacant`, color: '#7c3aed', live: false }
        : { text: `${buildingCount} ${buildingCount === 1 ? 'property' : 'properties'} \u2014 portfolio all clear \u2713`, color: '#16a34a', live: false };

  /* ── Smart action alerts ── */
  type ActionItem = {
    icon: string; color: string; lightColor: string; borderColor: string;
    title: string; meta: string; badge: string; badgeColor: string; badgeText: string;
    action: () => void;
  };

  const actionItems: ActionItem[] = ([
    unpaidNow.length > 0 && {
      icon: '🔴', color: '#dc2626', lightColor: '#fff1f1', borderColor: '#fecaca',
      title: `${unpaidNow.length} ${unpaidNow.length === 1 ? 'property' : 'properties'} \u2014 rent unpaid this month`,
      meta: unpaidNow.slice(0, 3).map(p => p.address + (p.room_number ? ` \u00b7 Rm ${p.room_number}` : '')).join(' \u00b7 ')
        + (unpaidNow.length > 3 ? ` +${unpaidNow.length - 3} more` : ''),
      badge: fmt(unpaidNow.reduce((s, p) => s + (p.amount_due || p.monthly_rent || 0), 0)),
      badgeColor: '#fee2e2', badgeText: '#b91c1c',
      action: () => navigate('/landlord/rent'),
    },
    partialNow.length > 0 && {
      icon: '🟡', color: '#d97706', lightColor: '#fffbeb', borderColor: '#fde68a',
      title: `${partialNow.length} partial payment${partialNow.length > 1 ? 's' : ''} \u2014 balance still owed`,
      meta: partialNow.slice(0, 2)
        .map(p => `${p.tenant_name}: ${fmt((p.amount_due || p.monthly_rent || 0) - (p.amount_paid || 0))} remaining`)
        .join(' \u00b7 '),
      badge: `${partialNow.length} partial`,
      badgeColor: '#fef3c7', badgeText: '#92400e',
      action: () => navigate('/landlord/rent'),
    },
    readyToRent.length > 0 && {
      icon: '✅', color: '#16a34a', lightColor: '#f0fdf4', borderColor: '#bbf7d0',
      title: `${readyToRent.length} ${readyToRent.length === 1 ? 'property' : 'properties'} ready to rent`,
      meta: readyToRent.slice(0, 3).map((p: any) => p.address).join(' \u00b7 '),
      badge: 'Market now',
      badgeColor: '#dcfce7', badgeText: '#15803d',
      action: () => navigate('/landlord/properties'),
    },
    voidWithDays.length > 0 && {
      icon: '🏚️', color: '#8b5cf6', lightColor: '#faf5ff', borderColor: '#e9d5ff',
      title: `${voidWithDays.length} vacant ${voidWithDays.length === 1 ? 'property' : 'properties'} \u2014 income lost`,
      meta: voidWithDays.slice(0, 2).map(p => `${p.address}: ${p.daysEmpty}d empty`).join(' \u00b7 '),
      badge: `\u00a3${voidWithDays.reduce((s, p) => s + p.estLost, 0).toLocaleString()} lost`,
      badgeColor: '#ede9fe', badgeText: '#6d28d9',
      action: () => navigate('/landlord/properties'),
    },
    totalOpenMaint > 0 && {
      icon: '🛠️', color: '#f97316', lightColor: '#fff7ed', borderColor: '#fed7aa',
      title: `${totalOpenMaint} open maintenance job${totalOpenMaint > 1 ? 's' : ''}`,
      meta: 'Review progress and assign contractors',
      badge: `${totalOpenMaint} open`,
      badgeColor: '#ffedd5', badgeText: '#c2410c',
      action: () => navigate('/landlord/maintenance'),
    },
    leasesExp60.length > 0 && {
      icon: '📄', color: '#0891b2', lightColor: '#f0f9ff', borderColor: '#bae6fd',
      title: `${leasesExp60.length} lease${leasesExp60.length > 1 ? 's' : ''} expiring within 60 days`,
      meta: leasesExp60.slice(0, 2)
        .map(p => `${p.address}${p.tenant_name ? ` (${p.tenant_name})` : ''} \u2014 ${p.days_to_expiry}d`)
        .join(' \u00b7 '),
      badge: 'Renew now',
      badgeColor: '#e0f2fe', badgeText: '#075985',
      action: () => navigate('/landlord/leases'),
    },
    leasesExp90.length > 0 && {
      icon: '📅', color: '#64748b', lightColor: '#f8fafc', borderColor: '#e2e8f0',
      title: `${leasesExp90.length} lease${leasesExp90.length > 1 ? 's' : ''} expiring in 60\u201390 days`,
      meta: leasesExp90.slice(0, 2).map(p => `${p.address} \u2014 ${p.days_to_expiry}d left`).join(' \u00b7 '),
      badge: 'Plan ahead',
      badgeColor: '#f1f5f9', badgeText: '#475569',
      action: () => navigate('/landlord/leases'),
    },
    totalExpiringCerts > 0 && {
      icon: '📋', color: '#dc2626', lightColor: '#fff1f1', borderColor: '#fecaca',
      title: `${totalExpiringCerts} certificate${totalExpiringCerts > 1 ? 's' : ''} expiring \u2014 legal compliance at risk`,
      meta: 'Gas Safety · EICR · EPC — expired certs can void insurance & prevent Section 21',
      badge: 'Act now',
      badgeColor: '#fee2e2', badgeText: '#991b1b',
      action: () => navigate('/landlord/certificates'),
    },
    (dashAlerts?.section13_pending?.length || 0) > 0 && {
      icon: '📈', color: '#0369a1', lightColor: '#f0f9ff', borderColor: '#bae6fd',
      title: `${dashAlerts.section13_pending.length} Section 13 notice${dashAlerts.section13_pending.length > 1 ? 's' : ''} due to be served`,
      meta: dashAlerts.section13_pending.slice(0, 2).map((r: any) => `${r.tenant_name}: £${Number(r.proposed_rent).toLocaleString()} from ${new Date(r.effective_date).toLocaleDateString('en-GB')}`).join(' · '),
      badge: 'Serve notice',
      badgeColor: '#e0f2fe', badgeText: '#075985',
      action: () => navigate('/landlord/leases'),
    },
    (dashAlerts?.referencing_pending?.length || 0) > 0 && {
      icon: '🔍', color: '#047857', lightColor: '#ecfdf5', borderColor: '#a7f3d0',
      title: `${dashAlerts.referencing_pending.length} tenant${dashAlerts.referencing_pending.length > 1 ? 's' : ''} — referencing in progress`,
      meta: dashAlerts.referencing_pending.slice(0, 2).map((r: any) => r.full_name).join(' · '),
      badge: 'Complete checks',
      badgeColor: '#d1fae5', badgeText: '#047857',
      action: () => navigate('/landlord/tenants'),
    },
  ] as (ActionItem | false)[]).filter(Boolean) as ActionItem[];

  /* ── Compliance health (4 RAG cards) ── */
  const complianceCards = [
    {
      label: 'Certificates',
      icon: totalExpiringCerts === 0 ? '\u2713' : '!',
      rag: (totalExpiringCerts === 0 ? 'green' : 'red') as 'green' | 'amber' | 'red',
      val: totalExpiringCerts === 0 ? 'All valid' : `${totalExpiringCerts} expiring`,
      sub: totalExpiringCerts === 0 ? 'Gas \u00b7 EPC \u00b7 EICR current' : 'Within 60 days \u2014 act now',
      action: () => navigate('/landlord/certificates'),
    },
    {
      label: 'Leases',
      icon: leasesExp60.length === 0 ? '\u2713' : leasesExp60.length <= 2 ? '~' : '!',
      rag: (leasesExp60.length === 0 ? 'green' : leasesExp60.length <= 2 ? 'amber' : 'red') as 'green' | 'amber' | 'red',
      val: leasesExp60.length === 0 ? 'All active' : `${leasesExp60.length} expiring`,
      sub: leasesExp60.length === 0
        ? (leasesExp90.length > 0 ? `${leasesExp90.length} due in 60\u201390d` : 'No renewals due soon')
        : 'Renewals needed within 60d',
      action: () => navigate('/landlord/leases'),
    },
    {
      label: 'Maintenance',
      icon: totalOpenMaint === 0 ? '\u2713' : totalOpenMaint <= 3 ? '~' : '!',
      rag: (totalOpenMaint === 0 ? 'green' : totalOpenMaint <= 3 ? 'amber' : 'red') as 'green' | 'amber' | 'red',
      val: totalOpenMaint === 0 ? 'No open jobs' : `${totalOpenMaint} open`,
      sub: totalOpenMaint === 0 ? 'All repairs resolved' : 'Outstanding repairs',
      action: () => navigate('/landlord/maintenance'),
    },
    {
      label: 'Occupancy',
      icon: occPct >= 95 ? '\u2713' : occPct >= 75 ? '~' : '!',
      rag: (occPct >= 95 ? 'green' : occPct >= 75 ? 'amber' : 'red') as 'green' | 'amber' | 'red',
      val: `${occPct}%`,
      sub: `${uniqueOcc} of ${uniqueAll} ${uniqueAll === 1 ? 'property' : 'properties'} let`,
      action: () => navigate('/landlord/properties'),
    },
  ];

  /* ── Rent bar colour ── */
  const barColor = collectPct === 100 ? '#16a34a' : collectPct >= 70 ? '#f59e0b' : '#dc2626';

  /* ── Nav tiles ── */
  const portalUrl = getTenantPortalLoginUrl();
  const copyPortalLink = () => {
    navigator.clipboard?.writeText(portalUrl);
    showToast('Tenant portal link copied');
  };

  const navTiles = [
    { label: 'Properties',  icon: '🏘️', path: '/landlord/properties',  color: '#1d4ed8', shadow: 'rgba(29,78,216,.35)' },
    { label: 'Tenants',     icon: '👤', path: '/landlord/tenants',      color: '#0e7490', shadow: 'rgba(14,116,144,.35)' },
    { label: 'Leases',      icon: '📄', path: '/landlord/leases',       color: '#7c3aed', shadow: 'rgba(124,58,237,.35)' },
    { label: 'Sales & Buy', icon: '🏗️', path: '/landlord/pipeline',     color: '#2563eb', shadow: 'rgba(37,99,235,.35)'  },
    { label: 'Rent',        icon: '💷', path: '/landlord/rent',         color: '#16a34a', shadow: 'rgba(22,163,74,.35)'  },
    { label: 'Maintenance', icon: '🛠️', path: '/landlord/maintenance',  color: '#ea580c', shadow: 'rgba(234,88,12,.35)'  },
    { label: 'Certs',       icon: '📋', path: '/landlord/certificates', color: '#0f766e', shadow: 'rgba(15,118,110,.35)' },
    { label: 'Accounting',  icon: '💰', path: '/landlord/accounting',   color: '#be185d', shadow: 'rgba(190,24,93,.35)'  },
    { label: 'Viewings',    icon: '📅', path: '/landlord/viewings',     color: '#4338ca', shadow: 'rgba(67,56,202,.35)'  },
  ];

  /* ── Loading state ── */
  if (loading) {
    return (
      <div className="ld-wrap">
        <style>{CSS}</style>
        <div className="ld-banner" style={{ justifyContent: 'center', padding: '28px' }}>
          <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 15, fontWeight: 600 }}>
            Loading your portfolio…
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260, color: '#94a3b8', fontSize: 14 }}>
          ⏳ Fetching dashboard data…
        </div>
      </div>
    );
  }

  /* ── Render ── */
  return (
    <div className="ld-wrap">
      <style>{CSS}</style>

      {/* ══════════════════════════ BANNER ══════════════════════════ */}
      <div className="ld-banner">
        <div className="ld-banner-left">
          <div className="ld-banner-logo">
            {businessLogo
              ? <img src={businessLogo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} />
              : (businessName?.charAt(0).toUpperCase() || '🏠')}
          </div>
          <div>
            <p className="ld-banner-greeting">{greeting()}</p>
            <h1 className="ld-banner-title">{businessName || 'ShahoSuite'}</h1>
            {businessAddress && (
              <p className="ld-banner-address"><span>📍</span>{businessAddress}</p>
            )}
          </div>
        </div>

        <div className="ld-banner-center">
          <p className="ld-banner-date">
            {new Date().toLocaleDateString('en-GB', {
              day: 'numeric', month: 'short', year: 'numeric',
            })}
          </p>
        </div>

        <div className="ld-banner-right">
          <div className="ld-banner-user">
            <p className="ld-banner-name">{userName || 'Landlord'}</p>
            <span className="ld-banner-role">{role || 'Admin'}</span>
          </div>
          <div className="ld-banner-notifs">
            <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 20, padding: '4px 8px', border: '1.5px solid rgba(255,255,255,0.24)' }}>
              <NotificationBell />
            </div>
            <div className="ld-banner-audit-btn" onClick={() => setShowAudit(true)} title="Activity log">
              📋
              {actionItems.length > 0 && (
                <span className="ld-banner-badge">{actionItems.length}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="ld-content">

        <div className="ld-portal-strip">
          <div>
            <div className="ld-portal-label">Tenant portal — share with your tenants</div>
            <div className="ld-portal-url">{getTenantPortalLabel()}</div>
            <div className="ld-portal-sub">Rent, maintenance requests & documents — one link for every tenant</div>
          </div>
          <div className="ld-portal-actions">
            <button type="button" className="ld-portal-btn" onClick={copyPortalLink}>Copy link</button>
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="ld-portal-btn-outline">Open portal ↗</a>
            <button type="button" className="ld-portal-btn-outline" onClick={() => navigate('/landlord/tenants')}>Manage tenants</button>
          </div>
        </div>

        {onboarding && !onboarding.complete && (
          <div className="ld-onboard">
            <div className="ld-onboard-head">
              <h3 className="ld-onboard-title">Get started with ShahoSuite</h3>
              <span className="ld-onboard-progress">{onboarding.progress} of {onboarding.total} complete</span>
            </div>
            <div className="ld-onboard-steps">
              {onboarding.steps.map((step: any) => (
                <div
                  key={step.id}
                  className={`ld-onboard-step${step.done ? ' done' : ''}`}
                  onClick={() => navigate(step.path)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && navigate(step.path)}
                >
                  <span className={`ld-onboard-check ${step.done ? 'done' : 'pending'}`}>{step.done ? '✓' : ''}</span>
                  <div>
                    <div className="ld-onboard-label">{step.label}</div>
                    {step.hint && !step.done && <div className="ld-onboard-hint">{step.hint}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && uniqueAll === 0 && (
          <div style={{
            marginBottom: 24, padding: '28px 32px', borderRadius: 16,
            background: 'linear-gradient(135deg,#eff6ff,#f0fdf4)',
            border: '2px dashed #93c5fd', textAlign: 'center',
          }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🏘️</div>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#1e3a8a' }}>Add your first property</h3>
            <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: 14 }}>
              Start tracking occupancy, rent, and compliance — it only takes a minute.
            </p>
            <button type="button" onClick={() => navigate('/landlord/properties/new')}
              style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
              + Add property
            </button>
          </div>
        )}

        {/* ══════════════════ SECTION 1 — FINANCIAL PULSE ══════════════════ */}
        <div className="ld-kpi-row">

          <div className="ld-kpi"
            style={{
              background: 'linear-gradient(135deg,#1d4ed8,#2563eb,#0ea5e9)',
              boxShadow: '0 6px 0 rgba(29,78,216,.45), 0 14px 28px rgba(29,78,216,.28)',
            }}
            onClick={() => navigate('/landlord/properties')}>
            <span className="ld-kpi-icon">🏘️</span>
            <div className="ld-kpi-label">Occupancy</div>
            <div className="ld-kpi-val">{occPct}%</div>
            <div className="ld-kpi-sub">{uniqueOcc} of {uniqueAll} let</div>
          </div>

          <div className="ld-kpi"
            style={{
              background: 'linear-gradient(135deg,#166534,#16a34a,#22c55e)',
              boxShadow: '0 6px 0 rgba(22,163,74,.45), 0 14px 28px rgba(22,163,74,.28)',
            }}
            onClick={() => navigate('/landlord/rent')}>
            <span className="ld-kpi-icon">💷</span>
            <div className="ld-kpi-label">Collected</div>
            <div className="ld-kpi-val">{fmt(totalReceived)}</div>
            <div className="ld-kpi-sub">{collectPct}% of {fmt(totalDue)}</div>
          </div>

          <div className="ld-kpi"
            style={{
              background: outstanding > 0
                ? 'linear-gradient(135deg,#991b1b,#dc2626,#ef4444)'
                : 'linear-gradient(135deg,#166534,#16a34a,#22c55e)',
              boxShadow: outstanding > 0
                ? '0 6px 0 rgba(185,28,28,.45), 0 14px 28px rgba(220,38,38,.28)'
                : '0 6px 0 rgba(22,163,74,.45), 0 14px 28px rgba(22,163,74,.28)',
            }}
            onClick={() => navigate('/landlord/rent')}>
            <span className="ld-kpi-icon">{outstanding > 0 ? '⚠️' : '✅'}</span>
            <div className="ld-kpi-label">Arrears</div>
            <div className="ld-kpi-val">{outstanding > 0 ? fmt(outstanding) : '—'}</div>
            <div className="ld-kpi-sub">
              {outstanding > 0
                ? `${unpaidNow.length + partialNow.length} tenant${(unpaidNow.length + partialNow.length) !== 1 ? 's' : ''} behind`
                : 'All rent collected'}
            </div>
          </div>

          <div className={`ld-kpi${totalOpenMaint > 0 ? ' ld-kpi-anim' : ''}`}
            style={{
              background: totalOpenMaint > 0
                ? 'linear-gradient(135deg,#c2410c,#ea580c,#f97316)'
                : 'linear-gradient(135deg,#1e293b,#334155,#475569)',
              boxShadow: totalOpenMaint > 0
                ? '0 6px 0 rgba(194,65,12,.45), 0 14px 28px rgba(234,88,12,.28)'
                : '0 6px 0 rgba(30,41,59,.35), 0 14px 28px rgba(0,0,0,.14)',
            }}
            onClick={() => navigate('/landlord/maintenance')}>
            <span className="ld-kpi-icon">{totalOpenMaint > 0 ? '🛠️' : '✅'}</span>
            <div className="ld-kpi-label">Maintenance</div>
            <div className="ld-kpi-val">{totalOpenMaint > 0 ? totalOpenMaint : '—'}</div>
            <div className="ld-kpi-sub">{totalOpenMaint > 0 ? 'Open jobs' : 'No open jobs'}</div>
          </div>

        </div>

        {/* ══════════════════ SECTION 2 — NEEDS ATTENTION ══════════════════ */}
        <div className="ld-section-head">
          <span className="ld-section-label">
            {actionItems.length > 0 ? `Needs Attention (${actionItems.length})` : 'Portfolio Status'}
          </span>
          <div className="ld-divider" />
        </div>

        {actionItems.length === 0 ? (
          <div className="ld-all-clear">
            <span className="ld-all-clear-icon">✅</span>
            <div>
              <div className="ld-all-clear-text">Nothing needs attention right now</div>
              <div className="ld-all-clear-sub">
                All rent collected · No expiring certs · No open maintenance · All leases active
              </div>
            </div>
          </div>
        ) : (
          <div className="ld-actions">
            {actionItems.map((item, i) => (
              <div key={i} className="ld-action-card"
                style={{ background: item.lightColor, borderColor: item.borderColor }}
                onClick={item.action}>
                <span className="ld-action-icon">{item.icon}</span>
                <div className="ld-action-body">
                  <div className="ld-action-title" style={{ color: item.color }}>{item.title}</div>
                  <div className="ld-action-meta">{item.meta}</div>
                </div>
                <span className="ld-action-badge"
                  style={{ background: item.badgeColor, color: item.badgeText }}>
                  {item.badge}
                </span>
                <span className="ld-action-arrow">→</span>
              </div>
            ))}
          </div>
        )}

        {/* ══════════════════ RENT COLLECTION STRIP ══════════════════ */}
        <div className="ld-rent-bar">
          <div className="ld-rent-bar-header">
            <div className="ld-rent-bar-title">💷 Rent Collection — {monthLabel}</div>
            <div className="ld-rent-bar-pct">{collectPct}% collected</div>
          </div>
          <div className="ld-rent-bar-track">
            <div className="ld-rent-bar-fill"
              style={{
                width: `${collectPct}%`,
                background: `linear-gradient(90deg, ${barColor}, #a7f3d0)`,
                boxShadow: `0 0 12px ${barColor}88`,
              }} />
          </div>
          <div className="ld-rent-bar-stats">
            <div>
              <div className="ld-rbs-label">Received</div>
              <div className="ld-rbs-val" style={{ color: '#34d399' }}>{fmt(totalReceived)}</div>
            </div>
            <div>
              <div className="ld-rbs-label">Arrears</div>
              <div className="ld-rbs-val" style={{ color: outstanding > 0 ? '#f87171' : '#94a3b8' }}>
                {outstanding > 0 ? fmt(outstanding) : '£0'}
              </div>
            </div>
            <div>
              <div className="ld-rbs-label">Expected</div>
              <div className="ld-rbs-val" style={{ color: '#fff' }}>{fmt(totalDue)}</div>
            </div>
            <div>
              <div className="ld-rbs-label">Void Units</div>
              <div className="ld-rbs-val" style={{ color: voidProps.length > 0 ? '#fbbf24' : '#94a3b8' }}>
                {voidProps.length}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════ SECTION 3 — COMPLIANCE HEALTH ══════════════════ */}
        <div className="ld-section-head">
          <span className="ld-section-label">Compliance &amp; Health</span>
          <div className="ld-divider" />
          <button className="ld-section-link" onClick={() => navigate('/landlord/certificates')}>
            Certificates →
          </button>
        </div>

        <div className="ld-comp-grid">
          {complianceCards.map((card, i) => {
            const c = RAG[card.rag];
            return (
              <div key={i} className="ld-comp-card"
                style={{ borderColor: c.border }}
                onClick={card.action}>
                <div className="ld-comp-dot" style={{ background: c.dot, color: c.text }}>
                  {card.icon}
                </div>
                <div>
                  <div className="ld-comp-label">{card.label}</div>
                  <div className="ld-comp-val" style={{ color: c.text }}>{card.val}</div>
                  <div className="ld-comp-sub">{card.sub}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ══════════════════ QUICK NAVIGATION ══════════════════ */}
        <div className="ld-section-head">
          <span className="ld-section-label">Quick Navigation</span>
          <div className="ld-divider" />
        </div>

        <div className="ld-quick-nav">
          {navTiles.map((t, i) => (
            <button key={i} className="ld-nav-btn"
              style={{ background: t.color, boxShadow: `0 6px 18px ${t.shadow}` }}
              onClick={() => navigate(t.path)}>
              <span>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>

      </div>

      {/* ══════════════════ ALERTS MODAL ══════════════════ */}
      {showAudit && (
        <div className="ld-modal-overlay" onClick={() => setShowAudit(false)}>
          <div className="ld-modal" onClick={e => e.stopPropagation()}>
            <div className="ld-modal-head">
              <span className="ld-modal-title">🔔 Alerts &amp; Actions</span>
              <button className="ld-modal-close" onClick={() => setShowAudit(false)}>✕</button>
            </div>
            <div className="ld-modal-body">
              <div className="ld-audit-list">
                {actionItems.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                    <div style={{ fontSize: 36, marginBottom: 12 }}>✨</div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>All clear!</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>No active alerts or actions needed.</div>
                  </div>
                ) : actionItems.map((item, i) => (
                  <div key={i} className="ld-audit-item"
                    onClick={() => { item.action(); setShowAudit(false); }}>
                    <span className="ld-audit-icon">{item.icon}</span>
                    <div>
                      <div className="ld-audit-msg" style={{ color: item.color }}>{item.title}</div>
                      <div className="ld-audit-time">{item.meta}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
